import React from 'react';
import { Truck, Receipt, Package, AlertCircle, Clock } from 'lucide-react';
import { CollapsibleSectionHeader } from './CollapsibleSectionHeader';
import {
  BasicInfoContent, StatusContent, InvoiceContent, UpdatedInvoiceContent,
  WeightAdjustmentContent, BillingContent, PaymentContent
} from './sections';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const SECTION_HEADER_CONFIG = {
  basic: { title: 'Basic Information', icon: Truck, bgColor: 'bg-gradient-to-r from-slate-50 to-slate-100', textColor: 'text-slate-700', borderColor: 'border-slate-300', accentColor: 'bg-slate-600' },
  invoice: { title: 'Invoice Details', icon: Receipt, bgColor: 'bg-gradient-to-r from-blue-50 to-sky-50', textColor: 'text-blue-700', borderColor: 'border-blue-300', accentColor: 'bg-blue-600' },
  status: { title: 'Delivery & Status', icon: Package, bgColor: 'bg-gradient-to-r from-emerald-50 to-green-50', textColor: 'text-emerald-700', borderColor: 'border-emerald-300', accentColor: 'bg-emerald-600' },
  updated_invoice: { title: 'Updated Invoice', icon: Receipt, bgColor: 'bg-gradient-to-r from-amber-50 to-yellow-50', textColor: 'text-amber-700', borderColor: 'border-amber-300', accentColor: 'bg-amber-500' },
  weight_adjustment: { title: 'Weight Adjustment', icon: Package, bgColor: 'bg-gradient-to-r from-indigo-50 to-violet-50', textColor: 'text-indigo-700', borderColor: 'border-indigo-300', accentColor: 'bg-indigo-600' },
  billing: { title: 'Billing & Follow-up', icon: Receipt, bgColor: 'bg-gradient-to-r from-orange-50 to-red-50', textColor: 'text-orange-700', borderColor: 'border-orange-300', accentColor: 'bg-orange-500' },
  payment: { title: 'Payment Tracking', icon: Clock, bgColor: 'bg-gradient-to-r from-purple-50 to-fuchsia-50', textColor: 'text-purple-700', borderColor: 'border-purple-300', accentColor: 'bg-purple-600' },
};

export default function SectionEditModeView({ contextValue }) {
  const {
    formData, handleFieldChange, errors, preventWheelChange,
    editingLoad, isSectionMode,
    shouldShowSection, isSectionEnabled, isSectionLocked, isSectionExpanded, isSectionVisible, toggleSection,
    sectionRefs,
    invoiceFile, setInvoiceFile, permitFile, setPermitFile, otherBills, setOtherBills,
    removePermitFile, setRemovePermitFile,
    generating, handleGenerateInvoiceClick, handleSendEmailClick,
    invoiceFile1, setInvoiceFile1, invoiceFile2, setInvoiceFile2,
    permitFile1, setPermitFile1, permitFile2, setPermitFile2,
    otherBills1, setOtherBills1, otherBills2, setOtherBills2,
    generating1, generating2, handleGenerateInvoice1Click, handleGenerateInvoice2Click,
    invoiceFile1Ref, invoiceFile2Ref, permitFile1Ref, permitFile2Ref,
    updatedInvoiceFile, setUpdatedInvoiceFile, removeUpdatedInvoiceFile, setRemoveUpdatedInvoiceFile,
    generatingUpdated, handleGenerateUpdatedInvoiceClick,
    additionInvoiceFile, setAdditionInvoiceFile, deductionInvoiceFile, setDeductionInvoiceFile,
    removeAdditionInvoiceFile, setRemoveAdditionInvoiceFile,
    removeDeductionInvoiceFile, setRemoveDeductionInvoiceFile,
    generatingAddition, handleGenerateAdditionInvoiceClick,
    generatingDeduction, handleGenerateDeductionInvoiceClick,
    labReportFile, setLabReportFile, removeLabReportFile, setRemoveLabReportFile,
    manualInvoiceErrors, clearManualInvoiceError,
    peekedNumber, peeking, peekNextInvoiceNumber,
    deliveryAttachments, setDeliveryAttachments, existingDeliveryFiles, setExistingDeliveryFiles,
  } = contextValue;

  const isRejected = formData.delivery_status === 'delivered' && formData.acceptance_status === 'rejected';

  const renderSectionHeader = (sectionName) => {
    if (isSectionMode) return null;
    const config = SECTION_HEADER_CONFIG[sectionName];
    if (!config) return null;
    return (
      <CollapsibleSectionHeader
        title={config.title} icon={config.icon}
        isExpanded={isSectionExpanded(sectionName)} onToggle={() => toggleSection(sectionName)}
        bgColor={config.bgColor} textColor={config.textColor} borderColor={config.borderColor} accentColor={config.accentColor}
      />
    );
  };

  return (
    <div>
      {/* Basic Info */}
      {shouldShowSection('basic') && isSectionEnabled('basic') && (
        <div ref={sectionRefs.basic} className="w-full overflow-hidden">
          {renderSectionHeader('basic')}
          {isSectionExpanded('basic') && (
            <div className="border-b pb-4">
              <BasicInfoContent formData={formData} handleFieldChange={handleFieldChange} isEditMode={!!editingLoad?.load_id} />
            </div>
          )}
        </div>
      )}

      {/* Invoice Details */}
      {shouldShowSection('invoice') && (isSectionEnabled('invoice') || isSectionLocked('invoice')) && (
        <div ref={sectionRefs.invoice} className="w-full overflow-hidden">
          {renderSectionHeader('invoice')}
          {isSectionExpanded('invoice') && (
            <div className="border-b pb-4 bg-blue-50 rounded-xl p-4">
              <InvoiceContent
                formData={formData} handleFieldChange={handleFieldChange} preventWheelChange={preventWheelChange} editingLoad={editingLoad}
                invoiceFile={invoiceFile} setInvoiceFile={setInvoiceFile}
                permitFile={permitFile} setPermitFile={setPermitFile}
                otherBills={otherBills} setOtherBills={setOtherBills}
                removePermitFile={removePermitFile} setRemovePermitFile={setRemovePermitFile}
                generating={generating} handleGenerateInvoiceClick={handleGenerateInvoiceClick}
                handleSendEmailClick={handleSendEmailClick}
                invoiceFile1={invoiceFile1} setInvoiceFile1={setInvoiceFile1}
                invoiceFile2={invoiceFile2} setInvoiceFile2={setInvoiceFile2}
                permitFile1={permitFile1} setPermitFile1={setPermitFile1}
                permitFile2={permitFile2} setPermitFile2={setPermitFile2}
                otherBills1={otherBills1} setOtherBills1={setOtherBills1}
                otherBills2={otherBills2} setOtherBills2={setOtherBills2}
                generating1={generating1} generating2={generating2}
                handleGenerateInvoice1Click={handleGenerateInvoice1Click}
                handleGenerateInvoice2Click={handleGenerateInvoice2Click}
                invoiceFile1Ref={invoiceFile1Ref} invoiceFile2Ref={invoiceFile2Ref}
                permitFile1Ref={permitFile1Ref} permitFile2Ref={permitFile2Ref}
                locked={isSectionLocked('invoice')}
                manualInvoiceErrors={manualInvoiceErrors} clearManualInvoiceError={clearManualInvoiceError}
                peekedNumber={peekedNumber} peeking={peeking} peekNextInvoiceNumber={peekNextInvoiceNumber}
              />
            </div>
          )}
        </div>
      )}

      {/* Delivery & Status */}
      {shouldShowSection('status') && (isSectionEnabled('status') || isSectionLocked('status')) && (
        <div ref={sectionRefs.status} className="w-full overflow-hidden">
          {renderSectionHeader('status')}
          {isSectionExpanded('status') && (
            <div className="border-b pb-4">
              <StatusContent formData={formData} handleFieldChange={handleFieldChange} locked={isSectionLocked('status')}
                deliveryAttachments={deliveryAttachments} setDeliveryAttachments={setDeliveryAttachments}
                existingDeliveryFiles={existingDeliveryFiles}
                onRemoveExistingFile={(index) => setExistingDeliveryFiles(prev => prev.filter((_, i) => i !== index))} />
            </div>
          )}
        </div>
      )}

      {/* Rejection Details (inline - only when rejected) */}
      {isRejected && (
        <div className="border-b pb-4 bg-red-50 rounded-xl p-4">
          <h3 className="text-sm font-bold text-red-700 uppercase tracking-wider mb-3 flex items-center gap-2" data-testid="rejection-details-header">
            <AlertCircle className="w-4 h-4" />Rejection Details
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Lab Report</Label>
              <Input value={formData.lab_report || ''} onChange={(e) => handleFieldChange('lab_report', e.target.value)} placeholder="Lab report details" className="h-11 rounded-xl" data-testid="lab-report-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Lab Report File</Label>
              <Input type="file" onChange={(e) => setLabReportFile(e.target.files[0])} className="h-11 rounded-xl" data-testid="lab-report-file-input" />
              {editingLoad?.lab_report_filename && !removeLabReportFile && (
                <div className="flex items-center justify-between bg-blue-50 rounded-lg px-3 py-2">
                  <p className="text-xs text-slate-600">Current: {editingLoad.lab_report_filename}</p>
                  <button type="button" onClick={() => setRemoveLabReportFile(true)} className="text-xs text-red-500 hover:text-red-700">Remove</button>
                </div>
              )}
              {removeLabReportFile && editingLoad?.lab_report_filename && (
                <div className="flex items-center justify-between bg-red-50 rounded-lg px-3 py-2">
                  <p className="text-xs text-red-600">File will be removed on save</p>
                  <button type="button" onClick={() => setRemoveLabReportFile(false)} className="text-xs text-blue-500 hover:text-blue-700">Undo</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Updated Invoice (BALAJI) */}
      {shouldShowSection('updated_invoice') && isSectionVisible('updated_invoice') && (isSectionEnabled('updated_invoice') || isSectionLocked('updated_invoice')) && (
        <div ref={sectionRefs.updated_invoice} className="w-full overflow-hidden">
          {renderSectionHeader('updated_invoice')}
          {isSectionExpanded('updated_invoice') && (
            <div className="border-b pb-4 bg-amber-50 rounded-xl p-4">
              <UpdatedInvoiceContent
                formData={formData} handleFieldChange={handleFieldChange} preventWheelChange={preventWheelChange}
                updatedInvoiceFile={updatedInvoiceFile} setUpdatedInvoiceFile={setUpdatedInvoiceFile}
                removeUpdatedInvoiceFile={removeUpdatedInvoiceFile} setRemoveUpdatedInvoiceFile={setRemoveUpdatedInvoiceFile}
                editingLoad={editingLoad}
                generatingUpdated={generatingUpdated} handleGenerateUpdatedInvoiceClick={handleGenerateUpdatedInvoiceClick}
                handleSendEmailClick={handleSendEmailClick}
                locked={isSectionLocked('updated_invoice')}
              />
            </div>
          )}
        </div>
      )}

      {/* Weight Adjustment (SVE) */}
      {shouldShowSection('weight_adjustment') && isSectionVisible('weight_adjustment') && (isSectionEnabled('weight_adjustment') || isSectionLocked('weight_adjustment')) && (
        <div ref={sectionRefs.weight_adjustment} className="w-full overflow-hidden">
          {renderSectionHeader('weight_adjustment')}
          {isSectionExpanded('weight_adjustment') && (
            <div className="border-b pb-4 bg-indigo-50 rounded-xl p-4">
              <WeightAdjustmentContent
                formData={formData} handleFieldChange={handleFieldChange} errors={errors} preventWheelChange={preventWheelChange} editingLoad={editingLoad}
                additionInvoiceFile={additionInvoiceFile} setAdditionInvoiceFile={setAdditionInvoiceFile}
                deductionInvoiceFile={deductionInvoiceFile} setDeductionInvoiceFile={setDeductionInvoiceFile}
                removeAdditionInvoiceFile={removeAdditionInvoiceFile} setRemoveAdditionInvoiceFile={setRemoveAdditionInvoiceFile}
                removeDeductionInvoiceFile={removeDeductionInvoiceFile} setRemoveDeductionInvoiceFile={setRemoveDeductionInvoiceFile}
                generatingAddition={generatingAddition} handleGenerateAdditionInvoiceClick={handleGenerateAdditionInvoiceClick}
                generatingDeduction={generatingDeduction} handleGenerateDeductionInvoiceClick={handleGenerateDeductionInvoiceClick}
                handleSendEmailClick={handleSendEmailClick}
                locked={isSectionLocked('weight_adjustment')}
              />
            </div>
          )}
        </div>
      )}

      {/* Billing & Follow-up */}
      {shouldShowSection('billing') && (isSectionEnabled('billing') || isSectionLocked('billing')) && (
        <div ref={sectionRefs.billing} className="w-full overflow-hidden">
          {renderSectionHeader('billing')}
          {isSectionExpanded('billing') && (
            <div className="border-b pb-4">
              <BillingContent formData={formData} handleFieldChange={handleFieldChange} errors={errors} preventWheelChange={preventWheelChange} locked={isSectionLocked('billing')} />
            </div>
          )}
        </div>
      )}

      {/* Payment Tracking */}
      {(shouldShowSection('payment') || !isSectionMode) && (isSectionEnabled('payment') || isSectionLocked('payment')) && (
        <div ref={sectionRefs.payment} className="w-full overflow-hidden">
          {renderSectionHeader('payment')}
          {isSectionExpanded('payment') && (
            <div className="border-b pb-4">
              <PaymentContent formData={formData} handleFieldChange={handleFieldChange} errors={errors} locked={isSectionLocked('payment')} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
